import EmblaCarousel from 'embla-carousel';

export default class Product {
  constructor() {
    document.querySelectorAll('.ird-product').forEach((section) => this.init(section));
  }

  init(section) {
    const sectionId = section.dataset.section;
    const dataEl = document.getElementById(`ProductData-${sectionId}`);

    if (!dataEl) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[Product] JSON parse error:', e, dataEl.textContent);
      return;
    }
    this.variants = data.variants;
    this.hasVariants = Boolean(data.has_variants);
    this.defaultVariantId = data.default_variant_id;

    this.section = section;
    this.mainImage = section.querySelector('[js-product-main-image]');
    this.thumbsContainer = section.querySelector('.ird-product__thumbs');
    this.swatches = section.querySelectorAll('[js-product-swatch]');
    this.sizeButtons = section.querySelectorAll('[js-product-size-button]');
    this.sizeSelect = section.querySelector('[js-product-size-select]');
    this.variantInput = section.querySelector('[js-product-variant-input]');
    this.priceEl = section.querySelector('[js-product-price]');
    this.atcBtn = section.querySelector('[js-product-atc]');
    this.colorLabel = section.querySelector('[js-product-color-label]');
    this.stickyBar = document.querySelector(`[js-product-sticky-bar][data-product-section="${sectionId}"]`)
      || section.querySelector('[js-product-sticky-bar]');
    this.stickyPriceEl = this.stickyBar?.querySelector('[js-product-sticky-price]') || null;
    this.stickyVariant = this.stickyBar?.querySelector('[js-product-sticky-variant]') || null;
    this.stickyAtcBtn = this.stickyBar?.querySelector('[js-product-sticky-atc]') || null;
    this.footer = document.querySelector('.ird-footer');
    this.lightbox = document.querySelector(`[js-product-lightbox][data-product-section="${sectionId}"]`)
      || section.querySelector('[js-product-lightbox]');
    this.lightboxThumbs = this.lightbox?.querySelector('[js-product-lightbox-thumbs]') || null;
    this.lightboxStage = this.lightbox?.querySelector('[js-product-lightbox-stage]') || null;
    this.lightboxClose = this.lightbox?.querySelector('[js-product-lightbox-close]') || null;
    this.lightboxOpen = false;

    this.attachSwatchEvents();
    this.attachSizeEvents();
    this.attachThumbEvents();
    this.attachPopState();
    this.syncFromUrl();
    this.syncThumbsVisibility();
    this.initCarousel();
    this.initStickyBar();
    this.initLightbox();
    this.syncStickyBarFromCurrentVariant();
  }

  syncThumbsVisibility() {
    if (!this.thumbsContainer) return;
    const thumbsCount = this.thumbsContainer.querySelectorAll('[js-product-thumb]').length;
    this.thumbsContainer.style.display = thumbsCount > 1 ? '' : 'none';
  }

  attachThumbEvents() {
    if (!this.thumbsContainer) return;

    this.thumbsContainer.addEventListener('click', (event) => {
      const thumb = event.target.closest('[js-product-thumb]');
      if (!thumb || !this.thumbsContainer.contains(thumb)) return;
      this.switchImage(thumb);
    });
  }

  switchImage(thumb) {
    const src = thumb.dataset.src;
    const alt = thumb.dataset.alt;

    if (!src || !this.mainImage) return;

    this.section
      .querySelectorAll('[js-product-thumb]')
      .forEach((t) => t.classList.remove('ird-product__thumb--active'));
    thumb.classList.add('ird-product__thumb--active');

    if (this.mainImage.currentSrc === src) return;

    this.mainImage.classList.add('-loading');

    const revealMainImage = () => {
      const show = () => this.mainImage.classList.remove('-loading');

      if (typeof this.mainImage.decode === 'function') {
        this.mainImage.decode().then(show).catch(show);
      } else {
        show();
      }
    };

    const applyMainImage = () => {
      this.mainImage.src = src;
      if (thumb.dataset.zoomSrc) this.mainImage.dataset.zoomSrc = thumb.dataset.zoomSrc;
      if (alt) this.mainImage.alt = alt;

      if (this.mainImage.complete) {
        revealMainImage();
      } else {
        this.mainImage.addEventListener('load', revealMainImage, { once: true });
      }
    };

    let applied = false;
    const applyOnce = () => {
      if (applied) return;
      applied = true;
      applyMainImage();
    };

    const preload = new Image();
    preload.onload = applyOnce;
    preload.onerror = () => this.mainImage.classList.remove('-loading');
    preload.src = src;

    if (preload.complete) applyOnce();
  }

  initCarousel() {
    this.carouselEl = this.section.querySelector('[js-product-gallery]');
    this.carouselViewport = this.section.querySelector('[js-product-gallery-viewport]');
    this.dotsContainer = this.section.querySelector('[js-product-gallery-dots]');

    if (!this.carouselEl || !this.carouselViewport) return;

    if (this.embla) this.embla.destroy();
    if (this.dotsContainer) this.dotsContainer.innerHTML = '';

    this.embla = EmblaCarousel(this.carouselViewport, { loop: false, align: 'start', dragFree: false });

    const slides = this.carouselEl.querySelectorAll('.ird-product__main-carousel-slide');
    const hasMultipleSlides = slides.length > 1;

    this.carouselDots = [];
    if (!hasMultipleSlides) {
      if (this.dotsContainer) this.dotsContainer.style.display = 'none';
    } else if (this.dotsContainer) {
      this.dotsContainer.style.display = '';
    }

    slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.classList.add('ird-product__dot');
      if (i === 0) dot.classList.add('ird-product__dot--active');
      dot.addEventListener('click', () => this.embla.scrollTo(i));
      if (this.dotsContainer) this.dotsContainer.appendChild(dot);
      this.carouselDots.push(dot);
    });

    this.embla.on('select', () => {
      const index = this.embla.selectedScrollSnap();
      this.carouselDots.forEach((d, i) => d.classList.toggle('ird-product__dot--active', i === index));
    });
  }

  switchImageByMediaId(mediaId) {
    if (!mediaId) return;

    const thumb = this.section.querySelector(`[js-product-thumb][data-media-id="${mediaId}"]`);
    if (thumb) this.switchImage(thumb);

    if (this.embla) {
      const slides = this.section.querySelectorAll('.ird-product__main-carousel-slide');
      slides.forEach((slide, i) => {
        if (slide.dataset.mediaId == mediaId) this.embla.scrollTo(i);
      });
    }
  }

  attachSwatchEvents() {
    this.swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => this.selectColor(swatch));
      swatch.addEventListener('mouseenter', () => this.previewColor(swatch));
      swatch.addEventListener('mouseleave', () => this.resetColorLabel());
    });
  }

  previewColor(swatch) {
    if (!this.colorLabel || swatch.disabled || swatch.classList.contains('ird-product__swatch--disabled')) {
      return;
    }

    const color = swatch.dataset.value;
    if (color) this.colorLabel.textContent = color;
  }

  resetColorLabel() {
    if (!this.colorLabel) return;

    const selectedColor = this.getSelectedColor();
    if (selectedColor) this.colorLabel.textContent = selectedColor;
  }

  attachSizeEvents() {
    this.sizeButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectSize(button));
    });
  }

  setActiveSize(size) {
    if (!size) return;

    this.sizeButtons.forEach((button) => {
      button.classList.toggle('ird-product__size-button--active', button.dataset.value === size);
    });

    if (this.sizeSelect && this.sizeSelect.value !== size) {
      this.sizeSelect.value = size;
    }
  }

  selectSize(button) {
    if (button.disabled || button.classList.contains('ird-product__size-button--disabled')) return;

    const size = button.dataset.value;
    if (!size) return;

    this.setActiveSize(size);
    this.onSizeChange();
  }

  getSelectedColor() {
    const activeSwatch = this.section.querySelector('[js-product-swatch].ird-product__swatch--active');
    return activeSwatch?.dataset.value || null;
  }

  getSelectedSize() {
    const activeButton = this.section.querySelector('[js-product-size-button].ird-product__size-button--active');
    return activeButton?.dataset.value || this.sizeSelect?.value || null;
  }

  findVariant({ color, size } = {}) {
    return this.variants.find((variant) => {
      const colorMatch = !color || variant.color === color;
      const sizeMatch = !size || variant.size === size;
      return colorMatch && sizeMatch;
    });
  }

  findVariantForCurrentOptions() {
    const color = this.getSelectedColor();
    const size = this.getSelectedSize();

    let variant = this.findVariant({ color, size });

    if (!variant && this.sizeSelect && size) {
      variant = this.variants.find((v) => v.size === size) || null;
    }

    if (!variant && color) {
      variant = this.findVariant({ color });
    }

    return variant || null;
  }

  updateSwatchesForSize(size) {
    if (!this.swatches.length) return;

    let firstAvailableColor = null;

    this.swatches.forEach((swatch) => {
      const color = swatch.dataset.value;
      const variant = size ? this.findVariant({ color, size }) : this.findVariant({ color });
      const isAvailable = Boolean(variant);

      swatch.disabled = !isAvailable;
      swatch.classList.toggle('ird-product__swatch--disabled', !isAvailable);

      if (isAvailable && !firstAvailableColor) {
        firstAvailableColor = color;
      }
    });

    const currentColor = this.getSelectedColor();
    const currentVariant = size
      ? this.findVariant({ color: currentColor, size })
      : this.findVariant({ color: currentColor });

    if (!currentVariant && firstAvailableColor) {
      const swatch = this.section.querySelector(
        `[js-product-swatch][data-value="${CSS.escape(firstAvailableColor)}"]`
      );
      if (swatch) {
        this.swatches.forEach((s) => s.classList.remove('ird-product__swatch--active'));
        swatch.classList.add('ird-product__swatch--active');
        if (this.colorLabel) this.colorLabel.textContent = firstAvailableColor;
      }
    }
  }

  applyVariant(variant, { updateHistory = true } = {}) {
    if (!variant) return;

    if (this.variantInput) {
      this.variantInput.value = variant.id;
      if (variant.available) {
        this.variantInput.removeAttribute('disabled');
      } else {
        this.variantInput.setAttribute('disabled', '');
      }
    }

    if (variant.size) this.setActiveSize(variant.size);

    this.updatePrice(variant);
    this.updateAtcButton(variant);
    this.updateStickyVariant(variant);
    this.updateOversizedNotice(variant);

    if (this.hasVariants) {
      this.renderGallery(variant);
    } else {
      this.switchImageByMediaId(variant.featured_media_id);
    }

    if (updateHistory) this.updateUrl(variant.id);
  }

  selectColor(swatch) {
    if (swatch.disabled || swatch.classList.contains('ird-product__swatch--disabled')) return;

    const color = swatch.dataset.value;
    if (!color) return;

    this.swatches.forEach((s) => s.classList.remove('ird-product__swatch--active'));
    swatch.classList.add('ird-product__swatch--active');

    if (this.colorLabel) this.colorLabel.textContent = color;

    const variant = this.findVariantForCurrentOptions();
    if (variant) this.applyVariant(variant);
  }

  onSizeChange() {
    const size = this.getSelectedSize();
    this.updateSwatchesForSize(size);

    const variant = this.findVariantForCurrentOptions();
    if (variant) this.applyVariant(variant);
  }

  syncVariantUI(variant) {
    if (!variant) return;

    if (variant.color) {
      const swatch = this.section.querySelector(
        `[js-product-swatch][data-value="${CSS.escape(variant.color)}"]`
      );
      if (swatch) {
        this.swatches.forEach((s) => s.classList.remove('ird-product__swatch--active'));
        swatch.classList.add('ird-product__swatch--active');
        if (this.colorLabel) this.colorLabel.textContent = variant.color;
      }
    }

    if (variant.size) this.setActiveSize(variant.size);
    if (this.sizeSelect) {
      this.updateSwatchesForSize(variant.size || this.getSelectedSize());
    }
  }

  buildVariantGallery(variant) {
    const images = [];
    const featuredSrc = variant?.featured_image?.src;

    if (featuredSrc) {
      images.push(variant.featured_image);
    }

    if (Array.isArray(variant?.gallery)) {
      variant.gallery.forEach((image) => {
        if (!image?.src) return;
        if (featuredSrc && image.src === featuredSrc) return;
        images.push(image);
      });
    }

    return images;
  }

  renderGallery(variant) {
    if (!this.hasVariants) return;
    if (!this.thumbsContainer) return;

    const gallery = this.buildVariantGallery(variant);
    this.thumbsContainer.innerHTML = '';

    if (!Array.isArray(gallery) || gallery.length === 0) {
      if (this.mainImage) this.mainImage.removeAttribute('src');

      if (this.carouselEl) {
        const container = this.carouselEl.querySelector('.ird-product__main-carousel-container');
        if (container) container.innerHTML = '';
      }
      this.syncThumbsVisibility();
      this.initCarousel();
      return;
    }

    gallery.forEach((image, index) => {
      const thumb = document.createElement('div');
      thumb.className = 'ird-product__thumb';
      if (index === 0) thumb.classList.add('ird-product__thumb--active');
      thumb.setAttribute('js-product-thumb', '');
      thumb.dataset.src = image.src || '';
      thumb.dataset.zoomSrc = image.zoom_src || this.toLightboxSrc(image.src || '');
      thumb.dataset.alt = image.alt || '';

      const media = document.createElement('div');
      media.className = 'ird-media';
      media.style.aspectRatio = '4/5';

      const img = document.createElement('img');
      img.src = image.thumb_src || image.src || '';
      img.alt = image.alt || '';
      img.loading = 'lazy';

      media.appendChild(img);
      thumb.appendChild(media);
      this.thumbsContainer.appendChild(thumb);
    });

    this.syncThumbsVisibility();

    if (this.mainImage) {
      this.mainImage.src = gallery[0].src || '';
      this.mainImage.dataset.zoomSrc = gallery[0].zoom_src || this.toLightboxSrc(gallery[0].src || '');
      this.mainImage.alt = gallery[0].alt || '';
    }

    if (this.carouselEl) {
      const container = this.carouselEl.querySelector('.ird-product__main-carousel-container');
      if (container) {
        container.innerHTML = '';

        gallery.forEach((image) => {
          const slide = document.createElement('div');
          slide.className = 'ird-product__main-carousel-slide';
          slide.dataset.src = image.src || '';
          slide.dataset.zoomSrc = image.zoom_src || this.toLightboxSrc(image.src || '');
          slide.dataset.alt = image.alt || '';

          const media = document.createElement('div');
          media.className = 'ird-media';
          media.style.aspectRatio = '4/5';

          const img = document.createElement('img');
          img.src = image.src || '';
          img.alt = image.alt || '';
          img.loading = 'lazy';

          media.appendChild(img);
          slide.appendChild(media);
          container.appendChild(slide);
        });
      }
    }

    this.initCarousel();

    if (this.lightboxOpen) {
      const activeIndex = Number(
        this.lightboxStage?.querySelector('.ird-product__lightbox-image.-active')?.dataset.index ?? 0
      );
      this.renderLightboxGallery(Number.isNaN(activeIndex) ? 0 : activeIndex);
    }
  }

  initLightbox() {
    if (!this.lightbox || !this.lightboxStage) return;

    this.lightboxActiveIndex = 0;
    this.lightboxScrolling = false;

    this.onLightboxKeydown = (event) => {
      if (event.key === 'Escape') this.closeLightbox();
    };

    this.onLightboxScroll = () => {
      if (this.lightboxScrolling) return;
      this.setActiveLightboxIndex(this.getLightboxVisibleIndex());
    };

    this.lightboxClose?.addEventListener('click', () => this.closeLightbox());
    this.lightboxStage.addEventListener('scroll', this.onLightboxScroll, { passive: true });

    this.lightboxThumbs?.addEventListener('click', (event) => {
      const thumb = event.target.closest('[js-product-lightbox-thumb]');
      if (!thumb || !this.lightboxThumbs.contains(thumb)) return;
      const index = Number(thumb.dataset.index);
      this.scrollLightboxTo(Number.isNaN(index) ? 0 : index);
    });

    this.mainImage?.addEventListener('click', () => this.openLightbox());

    this.section.addEventListener('click', (event) => {
      const slide = event.target.closest('.ird-product__main-carousel-slide');
      if (!slide || !this.section.contains(slide)) return;
      const slides = Array.from(this.section.querySelectorAll('.ird-product__main-carousel-slide'));
      this.openLightbox(slides.indexOf(slide));
    });
  }

  getGalleryItems() {
    const thumbs = this.thumbsContainer
      ? Array.from(this.thumbsContainer.querySelectorAll('[js-product-thumb]'))
      : [];

    if (thumbs.length) {
      return thumbs.map((thumb) => ({
        src: thumb.dataset.src || '',
        zoomSrc: thumb.dataset.zoomSrc || this.toLightboxSrc(thumb.dataset.src || ''),
        alt: thumb.dataset.alt || '',
        thumbSrc: thumb.querySelector('img')?.currentSrc || thumb.querySelector('img')?.src || thumb.dataset.src || '',
      })).filter((item) => item.src);
    }

    const slides = Array.from(this.section.querySelectorAll('.ird-product__main-carousel-slide'));
    if (slides.length) {
      return slides.map((slide) => ({
        src: slide.dataset.src || '',
        zoomSrc: slide.dataset.zoomSrc || this.toLightboxSrc(slide.dataset.src || ''),
        alt: slide.dataset.alt || '',
        thumbSrc: slide.dataset.src || '',
      })).filter((item) => item.src);
    }

    if (this.mainImage?.src) {
      const src = this.mainImage.getAttribute('src') || this.mainImage.src;
      return [{
        src,
        zoomSrc: this.mainImage.dataset.zoomSrc || this.toLightboxSrc(src),
        alt: this.mainImage.alt || '',
        thumbSrc: src,
      }];
    }

    return [];
  }

  normalizeImageKey(src) {
    if (!src) return '';

    try {
      const url = new URL(src, window.location.origin);
      return url.pathname.replace(/_\d+x\d*(?=\.)/g, '');
    } catch {
      return String(src).split('?')[0].replace(/_\d+x\d*(?=\.)/g, '');
    }
  }

  imagesMatch(a, b) {
    if (!a || !b) return false;
    if (a === b) return true;
    return this.normalizeImageKey(a) === this.normalizeImageKey(b);
  }

  toLightboxSrc(src) {
    if (!src) return '';

    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '1920');
      url.searchParams.delete('height');
      url.pathname = url.pathname.replace(/_\d+x\d*(?=\.[^/]+$)/, '_1920x');
      return url.href;
    } catch {
      return String(src)
        .replace(/([?&])width=\d+/gi, '$1width=1920')
        .replace(/_\d+x\d*(?=\.)/g, '_1920x');
    }
  }

  getActiveGalleryIndex(preferredIndex) {
    const items = this.getGalleryItems();
    if (!items.length) return 0;

    if (Number.isInteger(preferredIndex) && preferredIndex >= 0 && preferredIndex < items.length) {
      return preferredIndex;
    }

    const activeThumb = this.thumbsContainer?.querySelector('.ird-product__thumb--active[js-product-thumb]');
    if (activeThumb && this.thumbsContainer) {
      const thumbs = Array.from(this.thumbsContainer.querySelectorAll('[js-product-thumb]'));
      const index = thumbs.indexOf(activeThumb);
      if (index >= 0) return index;
    }

    if (this.mainImage) {
      const mainSrc = this.mainImage.getAttribute('src') || this.mainImage.currentSrc || this.mainImage.src;
      const bySrc = items.findIndex((item) => this.imagesMatch(item.src, mainSrc));
      if (bySrc >= 0) return bySrc;
    }

    if (this.embla) {
      const emblaIndex = this.embla.selectedScrollSnap();
      if (emblaIndex >= 0 && emblaIndex < items.length) return emblaIndex;
    }

    return 0;
  }

  renderLightboxGallery(activeIndex = 0) {
    if (!this.lightboxStage) return;

    const items = this.getGalleryItems();
    this.lightboxStage.innerHTML = '';
    if (this.lightboxThumbs) this.lightboxThumbs.innerHTML = '';

    if (!items.length) return;

    const safeIndex = Math.max(0, Math.min(activeIndex, items.length - 1));
    this.lightboxActiveIndex = safeIndex;

    if (this.lightboxThumbs) {
      if (items.length <= 1) {
        this.lightboxThumbs.style.display = 'none';
      } else {
        this.lightboxThumbs.style.display = '';
      }
    }

    items.forEach((item, index) => {
      const isActive = index === safeIndex;

      const img = document.createElement('img');
      img.className = 'ird-product__lightbox-image';
      if (isActive) img.classList.add('-active');
      img.src = item.zoomSrc || this.toLightboxSrc(item.src);
      img.alt = item.alt || '';
      img.dataset.index = String(index);
      img.dataset.src = item.src;
      img.dataset.zoomSrc = item.zoomSrc || this.toLightboxSrc(item.src);
      img.loading = index === 0 ? 'eager' : 'lazy';
      this.lightboxStage.appendChild(img);

      if (!this.lightboxThumbs || items.length <= 1) return;

      const thumb = document.createElement('div');
      thumb.className = 'ird-product__thumb';
      if (isActive) thumb.classList.add('ird-product__thumb--active');
      thumb.setAttribute('js-product-lightbox-thumb', '');
      thumb.dataset.index = String(index);
      thumb.dataset.src = item.src;
      thumb.dataset.alt = item.alt || '';

      const media = document.createElement('div');
      media.className = 'ird-media';
      media.style.aspectRatio = '4 / 5';

      const thumbImg = document.createElement('img');
      thumbImg.src = item.thumbSrc || item.src;
      thumbImg.alt = item.alt || '';
      thumbImg.loading = 'eager';

      media.appendChild(thumbImg);
      thumb.appendChild(media);
      this.lightboxThumbs.appendChild(thumb);
    });
  }

  scrollLightboxTo(index) {
    if (!this.lightboxStage) return;

    const images = Array.from(this.lightboxStage.querySelectorAll('.ird-product__lightbox-image'));
    if (!images.length) return;

    const safeIndex = Math.max(0, Math.min(index, images.length - 1));
    const target = images[safeIndex];

    this.lightboxScrolling = true;
    this.setActiveLightboxIndex(safeIndex);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    window.clearTimeout(this.lightboxScrollTimeout);
    this.lightboxScrollTimeout = window.setTimeout(() => {
      this.lightboxScrolling = false;
      this.setActiveLightboxIndex(this.getLightboxVisibleIndex());
    }, 450);
  }

  getLightboxVisibleIndex() {
    if (!this.lightboxStage) return 0;

    const images = Array.from(this.lightboxStage.querySelectorAll('.ird-product__lightbox-image'));
    if (!images.length) return 0;

    const stageRect = this.lightboxStage.getBoundingClientRect();
    const marker = stageRect.top + Math.min(120, stageRect.height * 0.2);
    let activeIndex = 0;

    images.forEach((img, index) => {
      if (img.getBoundingClientRect().top <= marker) activeIndex = index;
    });

    return activeIndex;
  }

  setActiveLightboxIndex(index) {
    if (!this.lightboxStage) return;

    const images = Array.from(this.lightboxStage.querySelectorAll('.ird-product__lightbox-image'));
    if (!images.length) return;

    const safeIndex = Math.max(0, Math.min(index, images.length - 1));
    if (safeIndex === this.lightboxActiveIndex) return;
    this.lightboxActiveIndex = safeIndex;

    images.forEach((img, i) => {
      img.classList.toggle('-active', i === safeIndex);
    });

    const thumbs = this.lightboxThumbs
      ? Array.from(this.lightboxThumbs.querySelectorAll('[js-product-lightbox-thumb]'))
      : [];

    thumbs.forEach((thumb, i) => {
      const isActive = i === safeIndex;
      thumb.classList.toggle('ird-product__thumb--active', isActive);
      if (isActive) {
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    });
  }

  openLightbox(preferredIndex) {
    if (!this.lightbox || !this.lightboxStage) return;

    const items = this.getGalleryItems();
    if (!items.length) return;

    const activeIndex = this.getActiveGalleryIndex(preferredIndex);
    this.renderLightboxGallery(activeIndex);

    this.lightbox.hidden = false;
    this.lightbox.setAttribute('aria-hidden', 'false');
    this.lightbox.classList.add('-open');
    document.body.classList.add('-product-lightbox-open');
    this.lightboxOpen = true;

    if (window.lenis && typeof window.lenis.stop === 'function') {
      window.lenis.stop();
    }

    document.addEventListener('keydown', this.onLightboxKeydown);
    this.lightboxClose?.focus();

    requestAnimationFrame(() => {
      this.scrollLightboxTo(activeIndex);
    });
  }

  closeLightbox() {
    if (!this.lightbox || !this.lightboxOpen) return;

    this.lightbox.classList.remove('-open');
    this.lightbox.hidden = true;
    this.lightbox.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('-product-lightbox-open');
    this.lightboxOpen = false;

    document.removeEventListener('keydown', this.onLightboxKeydown);

    const body = document.body;
    const stillLocked = body.classList.contains('-minicart-open')
      || body.classList.contains('-menu-open')
      || body.classList.contains('-desktop-menu-open')
      || body.classList.contains('-search-open');

    if (!stillLocked && window.lenis && typeof window.lenis.start === 'function') {
      window.lenis.start();
    }
  }

  updatePrice(variant) {
    if (!this.priceEl) return;

    if (variant.compare_at_price) {
      this.priceEl.innerHTML = `
        <div class="ird-price">
          <div class="ird-price__sale">
            <s class="ird-price__number ird-price__old">${variant.compare_at_price}</s>
            <span class="ird-price__number">${variant.price}</span>
          </div>
        </div>`;
    } else {
      this.priceEl.innerHTML = `
        <div class="ird-price">
          <span class="ird-price__number">${variant.price}</span>
        </div>`;
    }

    if (this.stickyPriceEl) {
      this.stickyPriceEl.innerHTML = this.priceEl.innerHTML;
    }
  }

  updateAtcButton(variant) {
    if (!this.atcBtn) return;
    if (variant.available) {
      this.atcBtn.removeAttribute('disabled');
      this.atcBtn.textContent = window.irdStrings?.addToCart || 'Add to cart';
    } else {
      this.atcBtn.setAttribute('disabled', '');
      this.atcBtn.textContent = window.irdStrings?.soldOut || 'Sold out';
    }

    if (!this.stickyAtcBtn) return;
    if (variant.available) {
      this.stickyAtcBtn.removeAttribute('disabled');
      this.stickyAtcBtn.textContent = window.irdStrings?.addToCart || 'Add to cart';
    } else {
      this.stickyAtcBtn.setAttribute('disabled', '');
      this.stickyAtcBtn.textContent = window.irdStrings?.soldOut || 'Sold out';
    }
  }

  updateStickyVariant(variant) {
    if (!this.stickyVariant || !variant) return;

    if (this.hasVariants && variant.title && variant.title !== 'Default Title') {
      this.stickyVariant.textContent = variant.title;
    } else {
      this.stickyVariant.textContent = '';
    }
  }

  updateOversizedNotice(variant) {
    const isOversized = Number(variant?.weight || 0) > 200000;
    const notice = this.section?.querySelector('[js-oversized-notice]');
    const availability = this.section?.querySelector('[js-product-availability]');
    const quoteTrigger = notice?.querySelector('[js-quote-drawer-open]');

    if (notice) notice.hidden = !isOversized;
    if (availability) availability.hidden = isOversized;

    if (quoteTrigger) {
      const title = this.section.querySelector('.ird-product__title')?.textContent?.trim() || '';
      const variantLabel =
        variant?.title && variant.title !== 'Default Title' ? ` / ${variant.title}` : '';
      const line = `${title}${variantLabel}`;
      quoteTrigger.setAttribute('data-quote-products', `${line}\n${window.location.href}`);
    }
  }

  getCurrentVariant() {
    if (!this.variantInput) return null;
    const variantId = parseInt(this.variantInput.value, 10);
    if (!variantId) return null;
    return this.variants.find((v) => v.id === variantId) || null;
  }

  syncStickyBarFromCurrentVariant() {
    const variant = this.getCurrentVariant();
    if (!variant) return;
    this.updateStickyVariant(variant);
    this.updateAtcButton(variant);
  }

  initStickyBar() {
    if (!this.stickyBar) return;
    this.lastScrollY = window.scrollY || 0;
    this.stickyBarScrollDir = 'down';
    this.updateStickyBarVisibility();
    window.addEventListener('scroll', () => this.updateStickyBarVisibility(), { passive: true });
    window.addEventListener('resize', () => this.updateStickyBarVisibility());
  }

  updateStickyBarVisibility() {
    if (!this.stickyBar) return;

    const scrollY = window.scrollY || 0;
    const delta = scrollY - this.lastScrollY;
    const threshold = 4;

    if (Math.abs(delta) > threshold) {
      this.stickyBarScrollDir = delta > 0 ? 'down' : 'up';
      this.lastScrollY = scrollY;
    }

    const introTop = this.section.getBoundingClientRect().top;
    const barHeight = this.stickyBar.offsetHeight || 0;
    let beforeFooter = true;

    if (this.footer) {
      const footerTop = this.footer.getBoundingClientRect().top;
      beforeFooter = footerTop > (window.innerHeight - barHeight);
    }

    const pastIntro = introTop <= 0;
    const shouldShow = pastIntro && beforeFooter && this.stickyBarScrollDir === 'down';
    this.stickyBar.classList.toggle('-visible', shouldShow);
  }

  updateUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.pushState({ variantId }, '', url.toString());
  }

  getDefaultVariant() {
    return this.variants.find((v) => v.id === this.defaultVariantId) || this.variants[0] || null;
  }

  resetToDefaultVariant() {
    const variant = this.getDefaultVariant();
    if (!variant) return;

    this.syncVariantUI(variant);
    this.applyVariant(variant, { updateHistory: false });
  }

  syncFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const variantId = parseInt(params.get('variant'), 10);

    if (!variantId) {
      this.resetToDefaultVariant();
      return;
    }

    const variant = this.variants.find((v) => v.id === variantId);
    if (!variant) {
      this.resetToDefaultVariant();
      return;
    }

    this.syncVariantUI(variant);
    this.applyVariant(variant, { updateHistory: false });
  }

  attachPopState() {
    window.addEventListener('popstate', () => this.syncFromUrl());
  }
}
